/**
 * Ornamentos do Painel da Gerência.
 *
 * Desenho vetorial próprio, não ícone de biblioteca: o assunto é madeira de
 * floresta plantada, e nenhum pacote de ícone tem pinha, anel de crescimento
 * ou grão de tábua. São formas simples o bastante para escalar sem perder o
 * traço e leves o bastante para não pesar no bundle.
 *
 * Todos herdam `currentColor` — quem posiciona decide a cor, e assim o mesmo
 * desenho serve de marca-d'água apagada ou de selo em destaque.
 */

/**
 * Anéis de crescimento.
 *
 * Os centros são deslocados de propósito: anel de árvore nunca é concêntrico
 * perfeito — cresce mais para o lado que pega sol, e é isso que dá a leitura
 * de madeira em vez de alvo de tiro.
 */
export function AneisDeCrescimento({ className }: { className?: string }) {
  const aneis = [
    { rx: 118, ry: 106, cx: 0, cy: 0, w: 1.6 },
    { rx: 100, ry: 90, cx: 3, cy: -2, w: 1 },
    { rx: 84, ry: 74, cx: 7, cy: -4, w: 1.9 },
    { rx: 68, ry: 60, cx: 10, cy: -5, w: 1 },
    { rx: 52, ry: 45, cx: 14, cy: -7, w: 1.5 },
    { rx: 37, ry: 32, cx: 17, cy: -8, w: 1 },
    { rx: 23, ry: 20, cx: 20, cy: -9, w: 1.7 },
    { rx: 11, ry: 9, cx: 22, cy: -10, w: 1 },
  ];
  return (
    <svg viewBox="-130 -120 260 240" className={className} aria-hidden="true"
      fill="none" stroke="currentColor">
      {aneis.map((a, i) => (
        <ellipse key={i} cx={a.cx} cy={a.cy} rx={a.rx} ry={a.ry}
          strokeWidth={a.w} transform={`rotate(-8 ${a.cx} ${a.cy})`} />
      ))}
      {/* Fenda de secagem, que toda tora tem. */}
      <path d="M 22 -10 L 76 -66" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Pinha.
 *
 * Escamas em fileiras alternadas — é assim que a pinha se organiza de fato,
 * cada fileira cobrindo a junta da anterior, como telha.
 */
export function Pinha({ className }: { className?: string }) {
  const fileiras = [
    { y: 15, n: 2, r: 7.5 },
    { y: 27, n: 3, r: 8.5 },
    { y: 40, n: 4, r: 9 },
    { y: 53, n: 3, r: 8.5 },
    { y: 65, n: 3, r: 7.5 },
    { y: 76, n: 2, r: 6 },
    { y: 85, n: 1, r: 4.5 },
  ];
  return (
    <svg viewBox="0 0 60 100" className={className} aria-hidden="true">
      {/* Talo */}
      <path d="M 30 12 L 30 4" stroke="currentColor" strokeWidth="2.4"
        strokeLinecap="round" fill="none" />
      {fileiras.map((f, i) =>
        Array.from({ length: f.n }, (_, j) => {
          const largura = f.n === 1 ? 0 : (f.n - 1) * f.r * 1.35;
          const x = 30 - largura / 2 + j * f.r * 1.35;
          return (
            <path
              key={`${i}-${j}`}
              d={`M ${x} ${f.y - f.r * 0.75}
                  C ${x + f.r} ${f.y - f.r * 0.5}, ${x + f.r} ${f.y + f.r * 0.55}, ${x} ${f.y + f.r * 0.8}
                  C ${x - f.r} ${f.y + f.r * 0.55}, ${x - f.r} ${f.y - f.r * 0.5}, ${x} ${f.y - f.r * 0.75} Z`}
              fill="currentColor"
              opacity={0.55 + (i % 2) * 0.35}
            />
          );
        }),
      )}
    </svg>
  );
}

/**
 * Pinheiro.
 *
 * Camadas assimétricas: galho de pinus não é triângulo isósceles, e a leve
 * irregularidade é o que separa árvore de sinal de trânsito.
 */
export function Pinheiro({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 80 120" className={className} aria-hidden="true">
      <path d="M 40 6 L 57 40 L 46 40 L 62 70 L 49 70 L 68 102 L 12 102 L 31 70 L 18 70 L 34 40 L 23 40 Z"
        fill="currentColor" opacity="0.9" />
      <path d="M 40 102 L 40 118" stroke="currentColor" strokeWidth="5"
        strokeLinecap="round" fill="none" opacity="0.65" />
    </svg>
  );
}

/**
 * Grão de tábua serrada.
 *
 * Linhas paralelas que se abrem em torno de um nó — o nó é o detalhe que faz
 * o desenho parecer madeira e não papel pautado.
 */
export function GraoDeMadeira({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 600 90" className={className} aria-hidden="true"
      fill="none" stroke="currentColor" preserveAspectRatio="none">
      <path d="M0 14 C 140 8, 250 4, 320 20 C 380 34, 470 20, 600 12" strokeWidth="1.5" />
      <path d="M0 30 C 150 24, 255 20, 322 34 C 385 46, 470 36, 600 28" strokeWidth="1" />
      <path d="M0 46 C 160 42, 262 40, 326 48 C 390 56, 470 52, 600 45" strokeWidth="1.6" />
      <path d="M0 62 C 150 60, 258 60, 324 62 C 392 64, 470 66, 600 62" strokeWidth="1" />
      <path d="M0 78 C 140 78, 250 80, 320 76 C 390 72, 470 80, 600 78" strokeWidth="1.4" />
      {/* O nó */}
      <ellipse cx="322" cy="47" rx="15" ry="9" strokeWidth="1.8" />
      <ellipse cx="322" cy="47" rx="7" ry="4" strokeWidth="1.2" />
    </svg>
  );
}

/** Tora empilhada — para estados vazios de estoque. */
export function ToraEmpilhada({ className }: { className?: string }) {
  const toras = [
    { cx: 26, cy: 62, r: 20 },
    { cx: 68, cy: 62, r: 20 },
    { cx: 47, cy: 27, r: 20 },
  ];
  return (
    <svg viewBox="0 0 94 92" className={className} aria-hidden="true"
      fill="none" stroke="currentColor">
      {toras.map((t, i) => (
        <g key={i}>
          <circle cx={t.cx} cy={t.cy} r={t.r} strokeWidth="2" />
          <circle cx={t.cx + 2} cy={t.cy - 1} r={t.r * 0.62} strokeWidth="1.2" opacity="0.75" />
          <circle cx={t.cx + 3} cy={t.cy - 2} r={t.r * 0.3} strokeWidth="1.2" opacity="0.55" />
        </g>
      ))}
    </svg>
  );
}
